import { useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FaArrowUp, FaChevronDown, FaChevronRight } from "react-icons/fa"
import { FaSortUp, FaSortDown } from "react-icons/fa6"
import { Box, HStack, Icon, IconButton, Input, Spinner, Stack, Table } from "@chakra-ui/react"
import { Section, Text } from "@/components"
import { useHotlist } from "@/hooks"
import type { HotlistEntry } from "@/types"

const tierColor = (t: string) =>
  t === "A" ? "#22c55e" : t === "B" ? "#3b82f6" : t === "C" ? "#f59e0b" : "#ef4444"
const tierBg = (t: string) =>
  t === "A" ? "rgba(34,197,94,0.1)" : t === "B" ? "rgba(59,130,246,0.1)" : t === "C" ? "rgba(245,158,11,0.1)" : "rgba(239,68,68,0.1)"

function formatPct(v: number | null): string {
  if (v == null) return "—"
  return `${(v * 100).toFixed(1)}%`
}

function formatPctSigned(v: number | null): string {
  if (v == null) return "—"
  const pct = v * 100
  const sign = pct > 0 ? "+" : ""
  return `${sign}${pct.toFixed(1)}%`
}

function formatDate(d: string | null): string {
  if (!d) return "—"
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  } catch {
    return d
  }
}

function formatDollars(v: number): string {
  if (v === 0) return "—"
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
  return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`
}

function pctChangeColor(v: number | null): string {
  if (v == null) return "text.muted"
  if (v > 0) return "#22c55e"
  if (v < 0) return "#ef4444"
  return "text.muted"
}

type SortKey = "company" | "tier" | "6mo" | "1yr" | "fmvChg" | "invested" | "allocation" | "total" | "closed" | "passed" | "open" | "closing" | "added"
type SortDir = "asc" | "desc"

const TIER_ORDER: Record<string, number> = { A: 1, B: 2, C: 3, D: 4 }

function getSortValue(entry: HotlistEntry, key: SortKey): string | number | null {
  switch (key) {
    case "company":    return entry.companyName.toLowerCase()
    case "tier":       return entry.tier ? (TIER_ORDER[entry.tier] ?? 99) : null
    case "6mo":        return entry.sixMonthGrowth
    case "1yr":        return entry.oneYearGrowth
    case "fmvChg":     return entry.fmvPctChange
    case "invested":   return entry.invested
    case "allocation": return entry.allocation
    case "total":      return entry.dealsTotal
    case "closed":     return entry.dealsClosed
    case "passed":     return entry.dealsPassed
    case "open":       return entry.dealsOpen
    case "closing":    return entry.dealsClosing
    case "added":      return entry.lastAddedAt
  }
}

function compareSortValues(a: string | number | null, b: string | number | null, dir: SortDir): number {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  const cmp = a < b ? -1 : a > b ? 1 : 0
  return dir === "asc" ? cmp : -cmp
}

const SortableHeader = ({
  label,
  sortKey,
  currentKey,
  currentDir,
  onSort,
  textAlign,
}: {
  label: string
  sortKey: SortKey
  currentKey: SortKey
  currentDir: SortDir
  onSort: (key: SortKey) => void
  textAlign?: "left" | "right"
}) => {
  const isActive = currentKey === sortKey
  return (
    <Table.ColumnHeader
      textAlign={textAlign}
      cursor="pointer"
      onClick={() => onSort(sortKey)}
      _hover={{ color: "white" }}
      userSelect="none"
    >
      <HStack gap={1} justifyContent={textAlign === "right" ? "flex-end" : "flex-start"}>
        <Text fontSize="xs">{label}</Text>
        {isActive && (
          <Icon
            as={(currentDir === "asc" ? FaSortUp : FaSortDown) as React.ElementType}
            boxSize="10px"
            color="white"
          />
        )}
      </HStack>
    </Table.ColumnHeader>
  )
}

export const HotlistComponent = () => {
  const { data: entries, isLoading } = useHotlist()
  const [search, setSearch] = useState("")
  const [hideNoDeals, setHideNoDeals] = useState(false)
  const [sortKey, setSortKey] = useState<SortKey>("company")
  const [sortDir, setSortDir] = useState<SortDir>("asc")
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const sectionRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const toggleExpand = (companyId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(companyId)) next.delete(companyId)
      else next.add(companyId)
      return next
    })
  }

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir(key === "company" || key === "tier" ? "asc" : "desc")
    }
  }

  const scrollToTop = () => {
    sectionRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  const filtered = useMemo(() => {
    let list = entries
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((e) => e.companyName.toLowerCase().includes(q))
    }
    if (hideNoDeals) {
      list = list.filter((e) => e.dealsTotal > 0)
    }
    return [...list].sort((a, b) =>
      compareSortValues(getSortValue(a, sortKey), getSortValue(b, sortKey), sortDir)
    )
  }, [entries, search, hideNoDeals, sortKey, sortDir])

  if (isLoading) {
    return (
      <Section>
        <HStack gap={3} py={10} justifyContent="center">
          <Spinner size="md" />
          <Text>Loading hotlist...</Text>
        </HStack>
      </Section>
    )
  }

  return (
    <Section ref={sectionRef} overflowY="auto" h="100vh">
      <Stack gap={4}>
        <HStack justifyContent="space-between" flexWrap="wrap">
          <HStack gap={3} alignItems="baseline">
            <Text fontSize="2xl" fontWeight="bold">Hotlist</Text>
            <Text fontSize="sm" color="text.muted">{entries.length} active companies</Text>
          </HStack>
          <HStack gap={3}>
            <Box
              as="button"
              px={3}
              py={1}
              borderRadius="md"
              fontSize="xs"
              fontWeight="600"
              cursor="pointer"
              bg={hideNoDeals ? "rgba(59,130,246,0.15)" : "transparent"}
              border={hideNoDeals ? "1px solid rgba(59,130,246,0.4)" : "1px solid"}
              borderColor={hideNoDeals ? "rgba(59,130,246,0.4)" : "border.default"}
              color={hideNoDeals ? "#3b82f6" : "text.muted"}
              onClick={() => setHideNoDeals((v) => !v)}
              _hover={{ bg: hideNoDeals ? "rgba(59,130,246,0.2)" : "hover.bg" }}
            >
              Has deals
            </Box>
            <Input
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              maxW="300px"
              size="sm"
            />
          </HStack>
        </HStack>

        <Box overflowX="auto">
          <Table.Root size="sm" variant="outline">
            <Table.Header>
              <Table.Row>
                <SortableHeader label="Company" sortKey="company" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Tier" sortKey="tier" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableHeader label="6mo Growth" sortKey="6mo" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
                <SortableHeader label="1yr Growth" sortKey="1yr" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
                <SortableHeader label="409A Chg" sortKey="fmvChg" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
                <SortableHeader label="Invested" sortKey="invested" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
                <SortableHeader label="Allocation" sortKey="allocation" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
                <SortableHeader label="Total" sortKey="total" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
                <SortableHeader label="Closed" sortKey="closed" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
                <SortableHeader label="Open" sortKey="open" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
                <SortableHeader label="Closing" sortKey="closing" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
                <SortableHeader label="Passed" sortKey="passed" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} textAlign="right" />
                <SortableHeader label="Added" sortKey="added" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filtered.map((entry) => {
                const isExpanded = expandedIds.has(entry.companyId)
                const hasDeals = entry.deals.length > 0
                return (
                  <>{/* Fragment for company row + optional deal sub-rows */}
                  <Table.Row
                    key={entry.companyId}
                    cursor={hasDeals ? "pointer" : "default"}
                    bg={entry.fmvPctChange != null && entry.fmvPctChange < 0 ? "rgba(239,68,68,0.08)" : undefined}
                    _hover={{ bg: entry.fmvPctChange != null && entry.fmvPctChange < 0 ? "rgba(239,68,68,0.15)" : "hover.bg" }}
                    onClick={() => hasDeals && toggleExpand(entry.companyId)}
                  >
                  <Table.Cell>
                    <HStack gap={1.5}>
                      {hasDeals && (
                        <Icon
                          as={(isExpanded ? FaChevronDown : FaChevronRight) as React.ElementType}
                          boxSize="10px"
                          color="text.muted"
                        />
                      )}
                      <Text
                        fontWeight="600"
                        fontSize="sm"
                        _hover={{ textDecoration: "underline" }}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/core/company/${entry.companyId}`)
                        }}
                        cursor="pointer"
                      >
                        {entry.companyName}
                      </Text>
                    </HStack>
                  </Table.Cell>
                  <Table.Cell>
                    {entry.tier ? (
                      <Box
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        px={2}
                        py={0.5}
                        borderRadius="full"
                        bg={tierBg(entry.tier)}
                        border={`1.5px solid ${tierColor(entry.tier)}`}
                        minW="28px"
                      >
                        <Text fontSize="11px" fontWeight="700" color={tierColor(entry.tier)}>
                          {entry.tier}
                        </Text>
                      </Box>
                    ) : (
                      <Text fontSize="xs" color="text.muted">—</Text>
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Text fontSize="sm">{formatPct(entry.sixMonthGrowth)}</Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Text fontSize="sm">{formatPct(entry.oneYearGrowth)}</Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <HStack gap={1.5} justifyContent="flex-end">
                      {entry.fmvStale && (
                        <Box
                          px={1.5}
                          py={0.5}
                          borderRadius="sm"
                          bg="rgba(245,158,11,0.15)"
                          border="1px solid rgba(245,158,11,0.4)"
                          lineHeight="1"
                        >
                          <Text fontSize="10px" fontWeight="700" color="#f59e0b">STALE</Text>
                        </Box>
                      )}
                      <Text fontSize="sm" color={pctChangeColor(entry.fmvPctChange)}>
                        {formatPctSigned(entry.fmvPctChange)}
                      </Text>
                    </HStack>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Text fontSize="sm">{formatDollars(entry.invested)}</Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Text fontSize="sm">{formatDollars(entry.allocation)}</Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Text fontSize="sm" fontWeight="600">{entry.dealsTotal || "—"}</Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Text fontSize="sm">{entry.dealsClosed || "—"}</Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Text fontSize="sm">{entry.dealsOpen || "—"}</Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Text fontSize="sm">{entry.dealsClosing || "—"}</Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Text fontSize="sm">{entry.dealsPassed || "—"}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm">{formatDate(entry.lastAddedAt)}</Text>
                  </Table.Cell>
                </Table.Row>
                {isExpanded && entry.deals.map((deal) => (
                  <Table.Row
                    key={deal.dealIdStr}
                    bg="rgba(255,255,255,0.02)"
                    cursor="pointer"
                    _hover={{ bg: "hover.bg" }}
                    onClick={() => navigate(`/core/deal/${deal.id}`)}
                  >
                    <Table.Cell colSpan={3} pl={10}>
                      <Text fontSize="xs" color="text.muted">{deal.dealIdStr}</Text>
                    </Table.Cell>
                    <Table.Cell colSpan={2}>
                      <Text fontSize="xs">{deal.clientName}</Text>
                    </Table.Cell>
                    <Table.Cell colSpan={2} textAlign="right">
                      <Text fontSize="xs">{deal.dealSize != null ? formatDollars(deal.dealSize) : "—"}</Text>
                    </Table.Cell>
                    <Table.Cell colSpan={3}>
                      <Text fontSize="xs">{deal.dealManager}</Text>
                    </Table.Cell>
                    <Table.Cell colSpan={3}>
                      <Text fontSize="xs" color="text.muted">{deal.stage}</Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
                </>
                )
              })}
              {filtered.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={13}>
                    <Text textAlign="center" color="text.muted" py={4}>
                      {search ? "No companies match your search" : "No active hotlist companies"}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        </Box>
      </Stack>

      <IconButton
        aria-label="Scroll to top"
        position="fixed"
        bottom={6}
        right={6}
        size="sm"
        variant="outline"
        onClick={scrollToTop}
      >
        <FaArrowUp />
      </IconButton>
    </Section>
  )
}
